import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { fetchResourceById } from '../../lib/contentful';

const ResourceDetailsPage = () => {
  const { id } = useParams();
  const [resource, setResource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      try {
        const data = await fetchResourceById(id);
        if (!mounted) return;
        if (data) {
          setResource(data);
        } else {
          setError('Resource not found');
        }
      } catch (e) {
        console.error(e);
        if (mounted) setError('Failed to load resource details');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [id]);

  return (
    <div className="page pt-20">
      <Navbar />
      
      <div className="px-6 md:px-10 py-12 max-w-4xl mx-auto min-h-[60vh]">
        <Link to="/resources" className="inline-flex items-center text-sm text-neutral-400 hover:text-white mb-8 transition-colors">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          Back to Resources
        </Link>

        {loading && (
          <div className="flex items-center justify-center h-48">
            <svg className="w-8 h-8 text-neutral-500 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
            </svg>
            <span className="ml-3 text-neutral-500">Loading resource...</span>
          </div>
        )}

        {error && (
          <div className="py-12 text-left">
            <p className="text-red-400 font-medium text-lg">Error</p>
            <p className="text-neutral-500 text-sm mt-2">{error}</p>
          </div>
        )}

        {!loading && resource && (
          <div className="bg-neutral-900/50 border border-neutral-800 rounded-3xl p-8 md:p-12">
            <div className="flex flex-wrap items-center gap-3 mb-6">
              {resource.category && (Array.isArray(resource.category) ? resource.category : [resource.category]).map((cat, idx) => (
                <span key={idx} className="text-xs font-semibold uppercase tracking-wider text-[#e3eed1cc] bg-neutral-800 px-3 py-1.5 rounded-full border border-neutral-700">
                  {cat}
                </span>
              ))}
            </div>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-6 leading-tight">
              {resource.title}
            </h1>
            
            <div className="prose prose-invert prose-neutral max-w-none mb-10">
              <p className="text-neutral-300 text-lg leading-relaxed">
                {resource.description}
              </p>
            </div>

            <div className="pt-8 border-t border-neutral-800">
              <a 
                href={resource.link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-6 py-3 bg-white text-black rounded-full font-medium text-sm hover:bg-neutral-200 transition-colors"
              >
                Visit Resource
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                </svg>
              </a>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ResourceDetailsPage;
